import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule, type MatSelect } from '@angular/material/select';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { Subject } from 'rxjs';

export interface UserAssignment {
  state: string;
  dealers: string[];
}

@Component({
  selector: 'app-user-assignment-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSelectSearchComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './user-assignment-selector.component.html',
  styleUrls: ['./user-assignment-selector.component.scss'],
})
export class UserAssignmentSelectorComponent implements OnChanges {
  /**
   * Example:
   * [
   *   { value:'MH', name:'Maharashtra' }
   * ]
   */
  @Input() states: any[] = [];

  /**
   * Existing assignments
   */
  @Input() assignments: UserAssignment[] = [];

  /**
   * Parent passes cached dealers
   *
   * {
   *    MH:[...],
   *    RJ:[...]
   * }
   */
  @Input() dealerMap: Record<string, any[]> = {};

  /**
   * Ask parent to fetch dealers
   */
  @Output() stateSelected = new EventEmitter<string>();

  /**
   * Emits latest assignment array
   */
  @Output() assignmentsChange = new EventEmitter<UserAssignment[]>();

  localAssignments: UserAssignment[] = [];

  dealerCodeFilterCtrl: FormControl = new FormControl();
  filteredDealerMap: Record<string, any[]> = {};
  private _onDestroy = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignments']) {
      this.localAssignments = this.assignments?.length
        ? JSON.parse(JSON.stringify(this.assignments))
        : [];
    }
    this.dealerCodeFilterCtrl.valueChanges.subscribe((search) => {
      Object.keys(this.dealerMap).forEach((state) => {
        const list = this.dealerMap[state];

        if (!search) {
          this.filteredDealerMap[state] = [...list];
        } else {
          this.filteredDealerMap[state] = list.filter((x) =>
            x.toString().includes(search)
          );
        }
      });
    });
  }

  filteredDealers(state: string) {
    return this.filteredDealerMap[state] || this.dealerMap[state] || [];
  }

  addState() {
    this.localAssignments.push({
      state: '',
      dealers: [],
    });

    this.emitChanges();
  }

  removeState(index: number) {
    this.localAssignments.splice(index, 1);

    this.emitChanges();
  }

  onStateChange(index: number) {
    const state = this.localAssignments[index].state;

    this.localAssignments[index].dealers = [];

    if (state) {
      this.stateSelected.emit(state);
    }

    this.emitChanges();
  }

  onDealerChange(select: MatSelect) {
    this.emitChanges();

    setTimeout(() => {
      if (!select.panelOpen) {
        select.open();
      }
    });
  }

  emitChanges() {
    this.assignmentsChange.emit(this.localAssignments);
  }

  dealers(state: string): any[] {
    return this.dealerMap[state] || [];
  }

  isStateDisabled(state: string, currentIndex: number): boolean {
    return this.localAssignments.some(
      (x, i) => i !== currentIndex && x.state === state
    );
  }
}
